import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Chip } from '@material-ui/core';
import EmptyAnswer from 'component/StandardAlert/no_answer';
import CustomizeSelectData from 'component/customize_select/customize_select';
import { TextErr } from 'component/customize_style_component/styled_title';
import CustomizeLayoutQuiz from 'component/layout_quiz/layout_quiz';
import { User } from 'component/stylist_profile_header/types';
import { useAuth } from 'contexts/AuthContext';
import { CREATE_ANSWER } from 'graph_ql/getQuiz/mutation';
import { GET_QUESTION_DATA, GET_QUIZ_RESPONSE } from 'graph_ql/getQuiz/queries';
import { GET_USER_BY_USERNAME } from 'graph_ql/users/queries';
import _, { get } from 'lodash'; // TODO: don't import all library
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { setPersonaDefault } from 'redux/authentication/user_detail';
import { RootState } from 'redux/store';
import { PATH } from 'routes/constant';
import { createFilterList, renderItem } from 'utils/convertListString';
import { getPersonaQuestion, getPersonaQuiz, getPersonaSubject, getValueSelect } from 'utils/utils';
import * as Yup from 'yup';
import { EQuizId, quizContextType } from 'types/quiz';
import useValidateAnswers from '../hooks/useValidateAnswers';

const formSchema = Yup.object().shape({
    personasId: Yup.array()
        .required('Personas is required')
        .min(
            3,
            'Please share three or more personality traits, characteristics, or personal values to help us tailor styles that truly reflect you.',
        ),
});
export interface IPaging {
    size: number;
    page: number;
}

export interface IAnswer {
    id: string;
}

const StylesRequestContainer = (props: { hideTitle?: boolean }) => {
    const { currentUser } = useAuth();
    const pathName = useLocation();
    const location = useLocation();
    const history = useHistory();
    const dispatch = useDispatch();
    const quiz = useSelector((state: RootState) => state.quiz.personasQuizId);
    const isUpdate = useSelector((state: RootState) => state.personas.isUpdate);
    const [quizData, setQuizData] = useState<any>(null);
    const [isUpdatePersonas, setIsUpdatePersonas] = useState(isUpdate);
    const { userName } = useParams<{ userName: string }>();
    const [user, setUser] = useState<User>();
    const [requiredQuestion, setRequiredQuestion] = useState(true);
    const [answerAlreadyUpdated, setAnswerUpdated] = useState(false);
    const [answerData, setAnswerData] = useState<IAnswer | null>(null);
    const isUpdateScreen = location.pathname.includes(PATH.MY_STYLE_PREFERENCES);

    const [personaSearchInput, setPersonaSearchInput] = useState('');
    const { data: genderData, loading: genderLoading } = useQuery(GET_QUIZ_RESPONSE, {
        fetchPolicy: 'network-only',
        variables: {
            input: {
                quizId: 'gender_quiz',
                contextType: quizContextType,
                contextId: EQuizId.GENDER_QUIZ,
                userId: currentUser?.uid,
            },
        },
    });
    const gender = useMemo(
        () => get(genderData, ['getQuizResponse', 'answers', 'gender_question', 'value']),
        [genderData],
    );

    const [getAnswerQuiz, { data: answerQuiz, loading }] = useLazyQuery(GET_QUIZ_RESPONSE, {
        fetchPolicy: 'network-only',
        onCompleted(data) {
            setRequiredQuestion(data.getQuizResponse?.questionsInfo[0]?.required);
            setQuizData(data.getQuizResponse);
        },
    });

    const [getUserByUserName, { loading: userLoading }] = useLazyQuery(GET_USER_BY_USERNAME, {
        onCompleted(data) {
            setUser(data?.getUserByUserName);
        },
    });

    useEffect(() => {
        if (userName) {
            getUserByUserName({
                variables: {
                    input: {
                        userName,
                    },
                },
            });
        }
    }, [userName, getUserByUserName]);

    useEffect(() => {
        if (isUpdate) {
            setIsUpdatePersonas(isUpdate);
        }
    }, [isUpdate]);

    const quizId = quiz || gender ? getPersonaQuiz(gender) : '';

    useEffect(() => {
        if (userName && user) {
            getAnswerQuiz({
                variables: {
                    input: {
                        quizId: user?.gender === 'male' ? 'personas_male_quiz' : 'personas_female_quiz',
                        contextType: quizContextType,
                        contextId: user?.gender === 'male' ? 'personas_male_quiz' : 'personas_female_quiz',
                        userId: user.id,
                    },
                },
            });
        }
        if (!userName && !user && quizId) {
            getAnswerQuiz({
                variables: {
                    input: {
                        quizId: quizId,
                        contextType: quizContextType,
                        contextId: quizId,
                        userId: currentUser.uid,
                    },
                },
            });
        }
    }, [userName, user, quiz, quizId, getAnswerQuiz, currentUser.uid]);

    useEffect(() => {
        if (answerQuiz) {
            const temp = Object.entries(answerQuiz?.getQuizResponse?.answers || {});
            const list: any = temp[0] && temp[0][1] ? temp[0][1] : [];
            setAnswerData(list);
            reset({
                personasId: renderItem(list?.value),
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [answerQuiz]);

    const { data: personasData } = useQuery(GET_QUESTION_DATA, {
        variables: {
            input: {
                subject: getPersonaSubject(gender),
                paging: { page: 0, size: 0 },
                title: personaSearchInput,
                field: 'title',
                value: '',
            },
        },
        skip: !gender,
    });

    const defaultValue = useMemo(() => {
        const temp = Object.entries(answerQuiz?.getQuizResponse?.answers || {});
        const list: any = temp[0] && temp[0][1] ? temp[0][1] : [];
        return {
            personasId: renderItem(list?.value),
        };
    }, [answerQuiz?.getQuizResponse?.answers]);
    const {
        control,
        handleSubmit,
        formState: { errors },
        watch,
        reset,
        getValues,
    } = useForm({
        defaultValues: defaultValue,
        resolver: yupResolver(formSchema),
    });

    const createAnswer = (input: {
        questionId: string;
        questionSubject: string;
        contextType: string;
        contextId: string;
        value: string;
        valueType: string;
        isSkipped?: boolean;
        id?: string;
    }) => {
        const variables = {
            input,
        };
        answerMutation({ variables });
    };
    const [answerMutation, { loading: loadingAnswer }] = useMutation(CREATE_ANSWER, {
        onCompleted(response) {
            dispatch(setPersonaDefault(createFilterList(response.createAnswer?.value)));
            if (isUpdatePersonas || pathName.pathname === PATH.MY_STYLE_PREFERENCES) {
            } else history.push(`${PATH.QUIZ_MANAGER}/?signup=${Date.now()}`);
        },
        refetchQueries: [
            {
                query: GET_QUIZ_RESPONSE,
                variables: {
                    input: {
                        quizId: getPersonaQuiz(gender) || quiz,
                        contextType: quizContextType,
                        contextId: getPersonaQuiz(gender) || quiz,
                        userId: currentUser?.uid,
                    },
                },
                fetchPolicy: 'network-only',
            },
        ],
        fetchPolicy: 'network-only',
        onError(error) {
            console.log('Create Failed: ', error);
        },
    });
    const personasOptions = useMemo(
        () => (personasData?.getQuestionData?.data || []).map(getValueSelect),
        [personasData?.getQuestionData?.data],
    );
    const input = useMemo(() => {
        return {
            questionId: getPersonaQuestion(gender),
            questionSubject: getPersonaSubject(gender),
            contextType: quizContextType,
            contextId: getPersonaQuiz(gender),
            valueType: 'STRING',
        };
    }, [gender]);
    const onSubmit = (data) => {
        if (data) {
            const newList = data.personasId.map((item) => item.value.toLowerCase());
            if (newList) {
                const value: string = data.personasId.reduce((val, item) => {
                    val += (val !== '' ? ',' : '') + item.value;
                    return val;
                }, '');
                createAnswer({ ...input, value, id: answerData?.id });
            }
        }
    };

    const isValidAnswer = useValidateAnswers(
        isUpdateScreen,
        getValues('personasId')?.length >= 1,
        answerAlreadyUpdated,
    );

    const handleSkip = () => {
        if (Boolean(!userName)) {
            createAnswer({ ...input, value: '', isSkipped: true });
        }
    };

    return (
        <CustomizeLayoutQuiz
            quizId={getPersonaQuiz(gender)}
            title={Boolean(!userName) ? quizData?.subTitle : ''}
            titlePage={!props.hideTitle && quizData?.title}
            handleAnswer={handleSubmit(onSubmit)}
            isUpdate={location.pathname.includes(PATH.MY_STYLE_PREFERENCES)}
            isDisplayContent={Boolean(!userName)}
            hideBottomNav={location.pathname === PATH.QUIZ_MANAGER}
            requiredQuestion={requiredQuestion}
            handleSkipQuestion={handleSkip}
            loading={loadingAnswer}
            containerLoading={loading || userLoading || genderLoading}
            nextBtnDisabled={!isValidAnswer}
        >
            <Box height={'100%'}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div style={{ padding: '0 24px', height: '100%', marginTop: 36 }}>
                        {Boolean(!userName) ? (
                            <Controller
                                name="personasId"
                                control={control}
                                rules={{
                                    required: { value: true, message: '' },
                                }}
                                render={({ field, fieldState }) => {
                                    return (
                                        <CustomizeSelectData
                                            data={personasOptions}
                                            handleSelectItem={(list) => {
                                                setAnswerUpdated(true);
                                                field.onChange(list);
                                            }}
                                            isHiddenLabel={true}
                                            error={!!fieldState.error}
                                            field="personas"
                                            placeholder="Select"
                                            onChangeInput={(input, value) => {
                                                _.debounce(() => {
                                                    setPersonaSearchInput(value);
                                                }, 500);
                                            }}
                                            defaultValue={watch('personasId')}
                                            anchorId="personas-select"
                                        />
                                    );
                                }}
                            />
                        ) : defaultValue?.personasId ? (
                            defaultValue?.personasId?.map((personas) => (
                                <Chip
                                    key={personas.value}
                                    label={personas.value}
                                    style={{
                                        borderRadius: 2,
                                        margin: 5,
                                        marginTop: 10,
                                        height: 25,
                                    }}
                                />
                            ))
                        ) : (
                            <></>
                        )}
                    </div>
                    {errors && errors?.personasId && (
                        <Box width={'100%'} textAlign="left" padding={'0 24px'}>
                            <TextErr>{(errors as any)?.personasId?.message}</TextErr>
                        </Box>
                    )}
                </form>
                {Boolean(userName) && !defaultValue?.personasId?.length && (
                    <Box height={'100%'} width="100%">
                        <EmptyAnswer title={'Personas'} userName={user?.firstName} />
                    </Box>
                )}
            </Box>
        </CustomizeLayoutQuiz>
    );
};
export default StylesRequestContainer;

export const PersonasQuiz = StylesRequestContainer;
